/* eslint-disable jsx-a11y/anchor-is-valid */
import { IonSpinner, IonToast, useIonToast } from '@ionic/react';
import { FullCommunityQuestion, FullPost, Post, objectEquals } from 'model';
import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router';
import { useDeletePostMutation, useGetPostQuery, useUpdatePostMutation } from '../api/PostApi';
import { CommunityQuestionEditFields } from '../components/CommunityQuestionEditFields';
import FormFooter from '../components/FormFooter';
import { Header } from '../components/Header';
import { IconButton } from '../components/IconButton';
import { IconCheck, IconClose } from '../components/QuirkIcons';
import { QuirkIonContent } from '../components/QuirkIonContent';
import { QuirkIonPage } from '../components/QuirkIonPage';
import { CommunityQuestionEditRules } from '../formConfigs/EditCommunityQuestionFieldsConfig';
import { useFormValues } from '../hooks/useFormValues';

export const EditCommunityQuestion: React.FC = () => {
	const { postid, question } = useParams<{ postid: Post['_id']; question: string }>();

	const { data: fetchPost } = useGetPostQuery(postid, { skip: !postid });
	const [post, setPost] = useState<FullPost | undefined | null>(fetchPost);
	const history = useHistory();
	const [deleteItem] = useDeletePostMutation();
	const [updatePost, { isError: updateIsError, isLoading: updateIsLoading, error: updateError }] =
		useUpdatePostMutation();

	const {
		setValues,
		formSuccess,
		formValues,
		validateForm,
		stateDataObject: newPost
	} = useFormValues<FullCommunityQuestion>(CommunityQuestionEditRules, post as FullCommunityQuestion, objectEquals);

	const [showToast] = useIonToast();
	const onSubmit = async () => {
		const compiledNoErrors = validateForm();
		if (compiledNoErrors && formValues && newPost) {
			if (post) {
				const result = await updatePost({
					post: {
						...post,
						...newPost,
						draft: false
					}
				});
				if (!('error' in result)) {
					setPost(result.data);
					showToast({
						color: 'success',
						message: 'Question has been updated',
						position: 'top',
						duration: 1500
					});
					history.push('/main/latest');
				} else {
					showToast({
						color: 'danger',
						message: 'Error updating question',
						position: 'top',
						duration: 1500
					});
				}
			}
		}
	};
	const onCancel = async () => {
		history.go(-1);

		if (fetchPost && fetchPost.draft) {
			await deleteItem(fetchPost._id);
		}
	};

	useEffect(() => {
		if (fetchPost) {
			if (question && fetchPost.title === '') {
				setPost({ ...fetchPost, title: question });
			} else {
				setPost(fetchPost);
			}
		}
		return () => setPost(undefined);
	}, [setPost, fetchPost, question]);

	return (
		<QuirkIonPage name="Edit Community Question" data={{ question: postid }}>
			<Header
				title="Community Question"
				left={<IconButton icon={<IconClose />} onClick={onCancel} />}
				right={
					<IconButton
						label="Done"
						disabled={!formSuccess}
						icon={<IconCheck />}
						onClick={onSubmit}
						testId="editPostDoneButton"
					/>
				}
			/>

			<QuirkIonContent flush padding>
				<>
					{post && formValues && (
						<CommunityQuestionEditFields
							setValues={setValues}
							setPost={setPost}
							formValues={formValues}
							post={post as FullCommunityQuestion}
						/>
					)}
					{post && formValues && (
						<FormFooter onCancel={onCancel} onSubmit={onSubmit} disableSubmit={!formSuccess} />
					)}
					<IonSpinner name="crescent" hidden={!updateIsLoading} />
					<IonToast
						isOpen={updateIsError}
						duration={1500}
						message={(updateError as any)?.data}
						position="top"
						color="danger"
					/>
				</>
			</QuirkIonContent>
		</QuirkIonPage>
	);
};
